<template>
    <page-loading-animation v-if="isLoading" :is-loading="isLoading" />

    <!--Page Content-->
    <div v-else>
        <!--Action Bar-->
        <div class="d-flex align-center mt-4">
            <!--Search-->
            <app-form-field
                form-type="textInput"
                append-icon="icons8-search"
                class="mr-4"
                :clearable="true"
                label="Event Name"
                style="width: 100%"
                v-model.trim="searchByEventName"
            />

            <!--Add Button-->
            <!--<app-btn @click.native="openRightPanel" class="mr-4" icon="add" label="Add"/>-->
            <div v-if="persistentSelectedIds.length" class="d-flex">
                <app-btn
                    @click.native="sendQrCodes(formattedTableRows)"
                    class="mr-2"
                    icon="qrCode"
                    :label="persistentSelectedIds.length > 1 ? 'Send QR Codes' : 'Send QR Code'"
                />
                <app-btn
                    @click.native="clearAllSelections"
                    class="mr-4"
                    color="error"
                    icon="close"
                    label="Clear Selection"
                />
            </div>

            <!--Filter Button - with a numbered badge-->
            <div style="position: relative">
                <app-btn @click.native="toggleFiltersVisibility" class="mr-4" color="appWhite" icon="filter" icon-color="primary" />
                <span v-if="computedNumberOfActiveFilters > 0" class="badge">{{ computedNumberOfActiveFilters }}</span>
            </div>

            <!--More Actions Menu-->
            <more-actions-menu @emitMenuSelection="handleEmittedMenuSelection" :menuList="moreActionsMenuOption" />
        </div>

        <!--Clear Filters-->
        <div v-if="computedNumberOfActiveFilters > 0" class="d-flex justify-end mt-4">
            <app-btn @click.native="clearFilters" icon="close" label="Clear Filters" />
        </div>

        <!--Table with fixed pagination-->
        <v-data-table
            v-if="$vuetify.breakpoint.width >= 600"
            class="appWhite rounded-lg mt-4"
            :headers="computedHeaders"
            :items="computedTableData"
            item-key="entityId"
            show-select
            v-model="selectedTableRows"
            :options.sync="dataTableOptions"
            @update:options="handleDataTableOptionsChange"
            @input="handleTableSelection"
            :server-items-length="1000"
            hide-default-footer
        >
            <!-- Override the header.data-table-select slot to hide the select-all checkbox -->
            <template v-slot:header.data-table-select></template>
            <template v-slot:footer>
                <div class="d-flex justify-end pa-2">
                    <div class="d-flex align-center">
                        <app-text size="small">{{ dataTableOptions.page }} of many</app-text>
                        <v-btn icon small :disabled="dataTableOptions.page === 1" @click="changePage(dataTableOptions.page - 1)">
                            <v-icon>mdi-chevron-left</v-icon>
                        </v-btn>
                        <v-btn icon small @click="changePage(dataTableOptions.page + 1)">
                            <v-icon>mdi-chevron-right</v-icon>
                        </v-btn>
                    </div>
                </div>
            </template>
            <!--ID-->
            <template v-slot:item.entityId="{ item }">
                <app-text size="small">{{ item.entityId }}</app-text>
            </template>

            <!--Event-->
            <template v-slot:item.parkingSpaceEvent="{ item }">
                <app-text size="small">{{ item.eventData?.eventName }}</app-text>
            </template>

            <!--Organisation/Individual Name-->
            <template v-slot:item.parkingSpaceOrganisation="{ item }">
                <!--Organisation-->
                <div v-if="item.organisationData?.entityId">
                    <app-text size="small">{{ item.organisationData?.organisationName }}</app-text>
                    <app-text color="grey9" size="small">Organisation</app-text>
                </div>

                <!--Individual-->
                <div v-if="item.individualData?.parkingSpaceIndividualName">
                    <app-text size="small">{{ item.individualData.parkingSpaceIndividualName }}</app-text>
                    <app-text color="grey9" size="small">Individual</app-text>
                </div>

                <!--Reserved-->
                <div v-if="item.reservedData?.length">
                    <app-text color="grey9" size="small">Reservations</app-text>
                </div>
            </template>

            <!--Car Parks-->
            <template v-slot:item.parkingSpaceCarPark="{ item }">
                <app-text v-for="item in item.parkingData" :key="item.carParkData?.entityId" size="small">
                    {{ item.carParkData.carParkName }}
                </app-text>
            </template>

            <!--Total Spaces-->
            <template v-slot:item.parkingSpaceTotal="{ item }">
                <div class="d-flex align-center justify-center">
                    <!--Line Totals-->
                    <div class="d-flex flex-column">
                        <div v-for="item in item.parkingData" :key="item.parkingSpaceData?.entityId" class="d-flex align-center">
                            <app-text size="small">
                                {{ getNumberOfSpacesRequired(item.parkingSpaceData).regularSpaces }}
                            </app-text>
                            <v-divider vertical class="mx-2" />
                            <app-text size="small">
                                {{ getNumberOfSpacesRequired(item.parkingSpaceData).disabledSpaces }}
                            </app-text>
                            <v-divider vertical class="mx-2" />
                        </div>
                    </div>

                    <!--Sum of Totals-->
                    <app-text size="small-bold">
                        {{ getTotalNumberOfSpacesRequired(item.parkingData) }}
                    </app-text>
                </div>
            </template>

            <!--Action Button-->
            <template v-slot:item.action="{ item }">
                <app-icon @click.native="openRightPanel(item)" class="cursorPointer" color="primary" icon="arrowForward" />
            </template>
        </v-data-table>

        <!--Mobile Cards-->
        <div v-if="$vuetify.breakpoint.width < 600">
            <!--No data message-->
            <app-text v-if="!computedTableData.length" class="text-center mt-4" color="grey9">You have no Event Parking to view </app-text>

            <event-parking-mobile-card v-for="item in computedTableData" :key="item.id" @click.native="openRightPanel(item)" :cardData="item" />
        </div>

        <!--Filters Panel ------------------------------------------------------------------------------------------ -->
        <filter-panel :is-filters-panel-visible="isFiltersPanelVisible" @toggle="toggleFiltersVisibility">
            <div class="pa-4">
                <!--Car Park-->
                <page-break-title title="Car Parks" />
                <div class="mt-4">
                    <app-form-field
                        form-type="autoComplete"
                        :deletable-chips="true"
                        :items="carParksData"
                        item-text="carParkName"
                        label="Car Parks"
                        :multiple="true"
                        :small-chips="true"
                        v-model="filterByCarParks"
                    />
                </div>

                <!--Parking Space Organisation-->
                <page-break-title class="mt-8" title="Individual Attendee Name" />
                <div class="mt-4">
                    <app-form-field form-type="textInput" :clearable="true" label="Attendee's Name" v-model.trim="filterByAttendee" />
                </div>

                <!--Spaces Required-->
                <!-- <page-break-title class="mt-8" title="Spaces Required" />
                <div class="mt-4">
                    <app-form-field form-type="textInput" :clearable="true" label="Spaces Required" v-model.trim="filterBySpacesRequired" />
                </div> -->
            </div>
        </filter-panel>

        <!--Right Side ----------------------------------------------------------------------------------- Right Side-->
        <transition
            enter-active-class="animate__animated animate__fadeInRight animate__faster"
            leave-active-class="animate__animated animate__fadeOutRight animate__faster"
            mode="out-in"
        >
            <div
                v-if="isRightPanelVisible"
                class="appGrey formShadow"
                style="position: absolute; top: 0; bottom: 0; right: 0; overflow: auto"
                :style="$vuetify.breakpoint.width < 600 ? 'width: 100%' : 'width: 50%'"
                v-model="isRightPanelVisible"
            >
                <!--Header-->
                <div class="d-flex align-center primary pa-4">
                    <!--No Name-->
                    <v-spacer />
                    <app-icon @click.native="closeRightPanel" class="cursorPointer" color="white" icon="close" size="32" />
                </div>

                <!--Action Bar-->
                <div class="d-flex align-center pa-4">
                    <v-spacer />

                    <!--Send QR Code Button-->
                    <app-btn
                        v-if="selectedItem.parkingData?.length"
                        @click.native="openSendQrCodeDialog"
                        color="primary"
                        icon="qrCode"
                        label="Send QR Code"
                    />

                    <!--Action Buttons-->
                    <edit-icon v-if="tabs === 'form'" @click.native="editItem" :isActive="!isReadOnly" />
                </div>

                <!--Tabs-->
                <v-tabs v-model="tabs">
                    <!--Form-->
                    <v-tab v-if="shouldFormBeVisible" href="#form">
                        <app-text size="small">Form</app-text>
                    </v-tab>

                    <!--Current-->
                    <v-tab v-if="selectedItem.parkingData?.length" @click="shouldFormBeVisible = false" href="#current">
                        <app-text size="small">Current</app-text>
                    </v-tab>
                </v-tabs>

                <!--Tabs Content-->
                <v-tabs-items class="appGrey" v-model="tabs">
                    <!--Form-->
                    <v-tab-item value="form">
                        <event-parking-form
                            v-if="selectedParkingType === 'Event Parking'"
                            @emitReloadPage="emittedReloadPage"
                            class="pa-4"
                            :carParksData="carParksData"
                            :eventsData="eventsData"
                            :formData="selectedParkingItem"
                            :isReadOnly="isReadOnly"
                            :organisationsData="organisationsData"
                        />
                    </v-tab-item>

                    <!--Current Parking-->
                    <v-tab-item value="current">
                        <event-parking-existing
                            @emitHandleSelectedParkingSpace="emittedHandleSelectedParkingSpace"
                            @emitReloadPage="emittedReloadPage"
                            :fullParkingData="selectedItem"
                        />
                    </v-tab-item>
                </v-tabs-items>
            </div>
        </transition>

        <!--Dialogs ------------------------------------------------------------------------------------------------ -->
        <app-dialog :is-visible="isSendQrCodeDialogVisible" @emitCloseSendQrCodeDialog="emittedCloseSendQrCodeDialog">
            <div class="appGrey pa-4">
                <app-text>
                    You are about to send this QR Code to the manager of this Organisation so they can distribute it internally.
                    <br />
                    <br />
                    On the day, attendees can have their QR Code scanned and be directed to the correct parking space.
                    <br />
                    <br />
                    <span class="font-weight-bold">Do you want to send this now?</span>
                </app-text>
                <v-divider class="my-4" />
                <div class="d-flex justify-space-between">
                    <!--Cancel-->
                    <app-btn @click.native="closeSendQrCodeDialog" color="grey" icon="cancel" label="Cancel" />

                    <!--Yes-->
                    <app-btn @click.native="handleSendSingleQrCode" color="green" icon="success" label="Yes, send" />
                </div>
            </div>
        </app-dialog>
    </div>
</template>

<script>
import ParkingCarParkForm from '@/views/parking/admin/parkingCarParks/parkingCarParkForm/ParkingCarParkForm.vue'
import EventParkingForm from '@/views/parking/admin/parkingSpaces/eventParking/eventParkingForm/EventParkingForm.vue'
import EventParkingMobileCard from '@/views/parking/admin/parkingSpaces/eventParking/eventParkingMobileCard/EventParkingMobileCard.vue'
import EventParkingExisting from '@/views/parking/admin/parkingSpaces/eventParking/eventParkingExisting/EventParkingExisting.vue'

export default {
    name: 'ParkingSpacesPage',

    components: { EventParkingExisting, EventParkingMobileCard, EventParkingForm, ParkingCarParkForm },

    data: () => ({
        filterByAttendee: '',
        filterByCarParks: [],
        filterBySpacesRequired: '',
        formattedTableRows: [],
        // Keep track of all selected row IDs across pagination
        persistentSelectedIds: [],
        isFiltersPanelVisible: false,
        isLoading: true,
        isReadOnly: false,
        isRightPanelVisible: false,
        isSendQrCodeDialogVisible: false,
        moreActionsMenuOption: [
            { name: 'Event Parking', icon: 'car' },
            { name: 'Export', icon: 'export' }
        ],
        searchByEventName: '',
        selectedItem: {},
        selectedTableRows: [],
        tableHeaders: [
            { text: 'ID', value: 'entityId', align: 'left', sortable: false, hidden: true },
            { text: 'Event', value: 'parkingSpaceEvent', align: 'left', sortable: false, hidden: false },
            { text: 'Attendee Name', value: 'parkingSpaceOrganisation', align: 'left', sortable: false, hidden: false },
            { text: 'Car Park(s)', value: 'parkingSpaceCarPark', align: 'left', sortable: false, hidden: false },
            {
                text: 'Spaces (Regular|Accessible|Total)',
                value: 'parkingSpaceTotal',
                align: 'center',
                sortable: false,
                hidden: false
            },
            { text: '', value: 'action', align: 'right', sortable: false, width: '48px' }
        ],
        tabs: '',

        selectedParkingType: '',
        selectedParkingItem: {},
        shouldFormBeVisible: false,

        carParksData: [],
        eventsData: [],
        organisationsData: [],
        parkingSpacesData: [],
        fullParkingSpacesData: [],

        // Data Table Options for Vuetify
        dataTableOptions: {
            page: 1,
            itemsPerPage: 10,
            sortBy: [],
            sortDesc: [],
            groupBy: [],
            groupDesc: []
        },

        // We can keep the pagination object for consistency with other code
        pagination: {
            page: 1,
            limit: 10
        }
    }),

    computed: {
        /**
         * Computed Export CSV
         *
         * Return the data and headers for the CSV export
         *
         * @returns {{headers: {}, data: *[]}}
         */
        computedExportCSV() {
            const t = this
            let data = []
            let dynamicHeaders = {}
            let headers = {}

            // Add the readable headers for the CSV columns
            headers = {
                entityId: 'ID',
                parkingSpaceEventId: 'Event',
                parkingSpaceParkingType: 'Parking Type',
                // parkingSpaceDaysRequired: 'Days Required',
                parkingSpaceCarParkId: 'Car Park',
                // parkingSpaceIndividualEmail: 'Individual Email',
                parkingSpaceIndividualName: 'Individual Name',
                parkingSpaceOrganisationId: 'Organisation',
                parkingSpaceNumberOfDisabledLargeVehiclesArrived: 'Disabled Large Vehicles Arrived',
                parkingSpaceNumberOfDisabledLargeVehiclesRequired: 'Disabled Large Vehicles Required',
                parkingSpaceNumberOfDisabledMediumVehiclesArrived: 'Disabled Medium Vehicles Arrived',
                parkingSpaceNumberOfDisabledMediumVehiclesRequired: 'Disabled Medium Vehicles Required',
                parkingSpaceNumberOfDisabledSmallVehiclesArrived: 'Disabled Small Vehicles Arrived',
                parkingSpaceNumberOfDisabledSmallVehiclesRequired: 'Disabled Small Vehicles Required',
                parkingSpaceNumberOfRegularLargeVehiclesArrived: 'Regular Large Vehicles Arrived',
                parkingSpaceNumberOfRegularLargeVehiclesRequired: 'Regular Large Vehicles Required',
                parkingSpaceNumberOfRegularMediumVehiclesArrived: 'Regular Medium Vehicles Arrived',
                parkingSpaceNumberOfRegularMediumVehiclesRequired: 'Regular Medium Vehicles Required',
                parkingSpaceNumberOfRegularSmallVehiclesArrived: 'Regular Small Vehicles Arrived',
                parkingSpaceNumberOfRegularSmallVehiclesRequired: 'Regular Small Vehicles Required',
                parkingSpaceNumberOfReservedSpaces: 'Reserved Spaces'
                // parkingSpaceReservedList: 'Reserved List',
                // parkingSpaceStatus: 'Status',
                // parkingSpaceUserId: 'User ID',
                // parkingSpaceVisitorName: 'Visitor Name',
                // parkingSpaceNotes: 'Notes',
            }

            // Add the data
            t.computedTableData.forEach((entry) => {
                const DATA_OBJECT = {
                    entityId: entry?.parkingData[0]?.parkingSpaceData?.entityId,
                    parkingSpaceEventId: entry?.eventData?.eventName,
                    parkingSpaceParkingType: entry?.parkingData[0]?.parkingSpaceData?.parkingSpaceParkingType,
                    // parkingSpaceDaysRequired: entry?.parkingData[0]?.parkingSpaceData?.parkingSpaceDaysRequired,
                    parkingSpaceCarParkId: entry?.parkingData[0]?.carParkData?.carParkName,
                    // parkingSpaceIndividualEmail: entry?.parkingData[0]?.parkingSpaceData?.parkingSpaceIndividualEmail,
                    parkingSpaceIndividualName: entry?.parkingData[0]?.parkingSpaceData?.parkingSpaceIndividualName,
                    parkingSpaceOrganisationId: entry?.organisationData?.organisationName,
                    parkingSpaceNumberOfDisabledLargeVehiclesArrived:
                        entry?.parkingData[0]?.parkingSpaceData?.parkingSpaceNumberOfDisabledLargeVehiclesArrived,
                    parkingSpaceNumberOfDisabledLargeVehiclesRequired:
                        entry?.parkingData[0]?.parkingSpaceData?.parkingSpaceNumberOfDisabledLargeVehiclesRequired,
                    parkingSpaceNumberOfDisabledMediumVehiclesArrived:
                        entry?.parkingData[0]?.parkingSpaceData?.parkingSpaceNumberOfDisabledMediumVehiclesArrived,
                    parkingSpaceNumberOfDisabledMediumVehiclesRequired:
                        entry?.parkingData[0]?.parkingSpaceData?.parkingSpaceNumberOfDisabledMediumVehiclesRequired,
                    parkingSpaceNumberOfDisabledSmallVehiclesArrived:
                        entry?.parkingData[0]?.parkingSpaceData?.parkingSpaceNumberOfDisabledSmallVehiclesArrived,
                    parkingSpaceNumberOfDisabledSmallVehiclesRequired:
                        entry?.parkingData[0]?.parkingSpaceData?.parkingSpaceNumberOfDisabledSmallVehiclesRequired,
                    parkingSpaceNumberOfRegularLargeVehiclesArrived:
                        entry?.parkingData[0]?.parkingSpaceData?.parkingSpaceNumberOfRegularLargeVehiclesArrived,
                    parkingSpaceNumberOfRegularLargeVehiclesRequired:
                        entry?.parkingData[0]?.parkingSpaceData?.parkingSpaceNumberOfRegularLargeVehiclesRequired,
                    parkingSpaceNumberOfRegularMediumVehiclesArrived:
                        entry?.parkingData[0]?.parkingSpaceData?.parkingSpaceNumberOfRegularMediumVehiclesArrived,
                    parkingSpaceNumberOfRegularMediumVehiclesRequired:
                        entry?.parkingData[0]?.parkingSpaceData?.parkingSpaceNumberOfRegularMediumVehiclesRequired,
                    parkingSpaceNumberOfRegularSmallVehiclesArrived:
                        entry?.parkingData[0]?.parkingSpaceData?.parkingSpaceNumberOfRegularSmallVehiclesArrived,
                    parkingSpaceNumberOfRegularSmallVehiclesRequired:
                        entry?.parkingData[0]?.parkingSpaceData?.parkingSpaceNumberOfRegularSmallVehiclesRequired,
                    parkingSpaceNumberOfReservedSpaces: entry?.parkingData[0]?.parkingSpaceData?.parkingSpaceNumberOfReservedSpaces
                    // parkingSpaceReservedList: entry?.parkingData[0]?.parkingSpaceData?.parkingSpaceReservedList,
                    // parkingSpaceStatus: entry?.parkingData[0]?.parkingSpaceData?.parkingSpaceStatus,
                    // parkingSpaceUserId: entry?.parkingData[0]?.parkingSpaceData?.parkingSpaceUserId,
                    // parkingSpaceVisitorName: entry?.parkingData[0]?.parkingSpaceData?.parkingSpaceVisitorName,
                    // parkingSpaceNotes: entry?.parkingData[0]?.parkingSpaceData?.parkingSpaceNotes,
                }

                // Parse the reserved list
                const RESERVED_LIST = JSON.parse(entry?.parkingData[0]?.parkingSpaceData?.parkingSpaceReservedList || '[]')

                // Update headers dynamically
                RESERVED_LIST.forEach((space) => {
                    // Add the header if it doesn't exist
                    if (!dynamicHeaders[`Reserved Space${space.reservedNumber}`])
                        dynamicHeaders[`Reserved Space${space.reservedNumber}`] = `Reserved Space ${space.reservedNumber}`

                    // Assign the reservedName to the respective space number
                    DATA_OBJECT[`Reserved Space${space.reservedNumber}`] = space.reservedName
                })

                data.push(DATA_OBJECT)
            })

            // Merge the static headers with the dynamic headers
            headers = { ...headers, ...dynamicHeaders }

            return { headers, data }
        },

        /**
         * Computed Headers
         *
         * Remove hidden headers and only show the actions column to those with access.
         *
         * @returns an array of header objects
         */
        computedHeaders() {
            const t = this
            let headers = t.tableHeaders

            headers = headers.filter((h) => !h.hidden)

            return headers
        },

        /**
         * Computed Number of Active Filters
         *
         * Count the number of active filters to display in the filters button badge.
         *
         * @returns {*}
         */
        computedNumberOfActiveFilters() {
            const t = this
            return [t.filterByAttendee ? 1 : 0, t.filterByCarParks.length, t.filterBySpacesRequired ? 1 : 0].reduce((acc, curr) => acc + curr, 0)
        },

        /**
         * Computed Table Data
         *
         * Apply client-side filtering for additional filters (car parks, attendee, spaces)
         *
         * @returns {array[{}]} an array of objects
         */
        computedTableData() {
            const t = this
            return t.fullParkingSpacesData
        }
    },

    methods: {
        /**
         * Handle Data Table Options Change
         *
         * This method is called when the data table options change (page, items per page, etc.)
         *
         * @param {Object} options - The new options object from the data table
         */
        handleDataTableOptionsChange(options) {
            const t = this

            // Check if page or itemsPerPage has changed
            if (options.page !== t.pagination.page || options.itemsPerPage !== t.pagination.limit) {
                // Update our pagination object
                t.pagination.page = options.page
                t.pagination.limit = options.itemsPerPage

                // Reload data with new pagination values
                t.loadFullParkingSpacesData().then(() => {
                    // After data is loaded, restore selections based on persistentSelectedIds
                    t.restoreSelections()
                })
            }
        },

        /**
         * Change Page (for mobile view)
         *
         * Change the current page and reload the data.
         *
         * @param {Number} page - The page number to change to
         */
        changePage(page) {
            const t = this

            // Update both pagination objects
            t.pagination.page = page
            t.dataTableOptions.page = page

            // Reload data with new page
            t.loadFullParkingSpacesData().then(() => {
                // After data is loaded, restore selections based on persistentSelectedIds
                t.restoreSelections()
            })
        },

        /**
         * Change Items Per Page (for mobile view)
         *
         * Change the number of items per page and reload the data.
         *
         * @param {Number} limit - The new limit of items per page
         */
        changeItemsPerPage(limit) {
            const t = this

            // Update both pagination objects
            t.pagination.limit = limit
            t.dataTableOptions.itemsPerPage = limit

            // Reset to first page
            t.pagination.page = 1
            t.dataTableOptions.page = 1

            // Reload data with new limit 
            t.loadFullParkingSpacesData().then(() => {
                // After data is loaded, restore selections based on persistentSelectedIds
                t.restoreSelections()
            })
        },

        /**
         * Clear Filters
         *
         * Clear all the filters.
         */
        clearFilters() {
            const t = this

            t.filterByAttendee = ''
            t.filterByCarParks = []
            t.filterBySpacesRequired = ''

            // Reset to page 1 and reload data
            t.pagination.page = 1
            t.dataTableOptions.page = 1
            t.loadFullParkingSpacesData()
        },

        /**
         * Close Right Panel
         *
         * Close the right panel.
         */
        closeRightPanel() {
            const t = this

            t.tabs = ''
            t.selectedItem = {}
            t.isRightPanelVisible = false
        },

        /**
         * Close Send Qr Code Dialog
         *
         * Close the send QR code dialog.
         */
        closeSendQrCodeDialog() {
            const t = this

            t.isSendQrCodeDialogVisible = false
        },

        /**
         * Edit Item
         *
         * Toggle the editability of the form.
         */
        editItem() {
            const t = this

            t.isReadOnly = !t.isReadOnly
        },

        /**
         * Emitted Close Send Qr Code Dialog
         *
         * Call to close the send QR code dialog.
         */
        emittedCloseSendQrCodeDialog() {
            const t = this

            t.closeSendQrCodeDialog()
        },

        /**
         * Emitted Handle Selected Parking Space
         *
         * Assign the selected parking space to the selected parking item, and show the form.
         * @param parkingSpace
         */
        emittedHandleSelectedParkingSpace(parkingSpace) {
            const t = this

            t.selectedParkingItem = { ...parkingSpace }
            t.tabs = 'form'
            t.shouldFormBeVisible = true
        },

        /**
         * Emitted Reload Page
         *
         * Close the right panel and reload the data to refresh the page.
         */
        emittedReloadPage() {
            const t = this

            t.closeRightPanel()
            t.loadData()
        },

        /**
         * Export Data
         *
         * Export the data to CSV
         */
        async exportData() {
            const t = this
            t.loading = true
            // Show loading indicator
            if (t.$store && t.$store.commit) {
                t.$store.commit('setLoading', true)
            }

            try {
                // Build Redis search query - start with base filter
                const searchQuery = ['@isDeleted:{false}']

                // Add event name filter
                if (t.searchByEventName) {
                    // Find matching event IDs first
                    const matchingEvents = t.eventsData.filter((event) => event.eventName.toLowerCase().includes(t.searchByEventName.toLowerCase()))

                    if (matchingEvents.length > 0) {
                        // FIXED: Format the OR query properly for Redis Search
                        // The key issue is we need a proper Redis OR syntax
                        if (matchingEvents.length === 1) {
                            // Simple case - just one event
                            searchQuery.push(`@parkingSpaceEventId:{${matchingEvents[0].entityId}}`)
                        } else {
                            // Multiple events - use proper Redis OR syntax without parentheses
                            // Format: @field:({value1}|{value2}|{value3})
                            const eventIds = matchingEvents.map((event) => event.entityId).join('|')
                            searchQuery.push(`@parkingSpaceEventId:{${eventIds}}`)
                        }
                    } else {
                        // No matching events, return empty result early
                        t.fullParkingSpacesData = []
                        return
                    }
                }

                if (t.filterByAttendee) {
                    // Escape any special characters in the search term
                    const escapedAttendee = t.filterByAttendee.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
                    // Redis text search for partial matches - FIXED syntax
                    // only if filterByAttendee is not empty and more than 1 letter
                    if (escapedAttendee.length > 1) {
                        // FIXED: Format the OR query properly for Redis Search
                        // The key issue is we need a proper Redis OR syntax
                        // Format: @field:({value1}|{value2}|{value3})
                        searchQuery.push(`@parkingSpaceIndividualName:${escapedAttendee}*`)
                    }
                }

                // Add car park filter
                if (t.filterByCarParks && t.filterByCarParks.length > 0) {
                    const carParkIds = t.filterByCarParks
                        .map((carParkName) => {
                            const carPark = t.carParksData.find((cp) => cp.carParkName === carParkName)
                            return carPark ? carPark.entityId : null
                        })
                        .filter(Boolean)

                    if (carParkIds.length > 0) {
                        // FIXED: Format the OR query properly for Redis Search
                        if (carParkIds.length === 1) {
                            // Simple case - just one car park
                            searchQuery.push(`@parkingSpaceCarParkId:{${carParkIds[0]}}`)
                        } else {
                            // Multiple car parks - use proper Redis OR syntax
                            const carParkIdsString = carParkIds.join('|')
                            searchQuery.push(`@parkingSpaceCarParkId:{${carParkIdsString}}`)
                        }
                    }
                }
                // Join all parts of the query with spaces (Redis AND operator)
                const queryString = searchQuery.join(' ')

                // For export, we need to get a large batch of records
                const RESPONSE = await t.MIX_redis_getFullParkingSpaces({
                    query: queryString,
                    page: 1,
                    pageSize: 9999 // Large limit to get all records
                })

                if (RESPONSE.hasErrors) {
                    console.error('Error getting data for export: ', RESPONSE.error)
                    t.$sharedState.errorMessage = 'There was a problem exporting data, please try again.'
                    return
                }

                // Create a temporary computedTableData with all records
                const allData = RESPONSE.data
                const originalFullData = t.fullParkingSpacesData
                t.fullParkingSpacesData = allData

                // Export using the computedExportCSV which depends on computedTableData
                t.MIX_exportDocuments(t.computedExportCSV.headers, 'Parking', t.computedExportCSV.data)

                // Restore the original paginated data
                t.fullParkingSpacesData = originalFullData
                t.loading = false
            } catch (error) {
                console.error('Error in export: ', error)
                t.$sharedState.errorMessage = 'There was a problem exporting data, please try again.'
            } finally {
                // Hide loading indicator
                if (t.$store && t.$store.commit) {
                    t.$store.commit('setLoading', false)
                }
            }
        },

        /**
         * Get Number Of Spaces Required
         *
         * Calculate the number of spaces required for the given item (regular and disabled).
         *
         * @param item - the item to calculate the number of spaces required for
         * @returns {{disabledSpaces: (*|number), totalSpaces: (*|number), regularSpaces: (*|number)}}
         */
        getNumberOfSpacesRequired(item) {
            let regularSpaces = 0
            let disabledSpaces = 0
            let totalSpaces = 0

            regularSpaces =
                item.parkingSpaceNumberOfRegularSmallVehiclesRequired +
                item.parkingSpaceNumberOfRegularMediumVehiclesRequired +
                item.parkingSpaceNumberOfRegularLargeVehiclesRequired
            disabledSpaces =
                item.parkingSpaceNumberOfDisabledSmallVehiclesRequired +
                item.parkingSpaceNumberOfDisabledMediumVehiclesRequired +
                item.parkingSpaceNumberOfDisabledLargeVehiclesRequired
            totalSpaces = regularSpaces + disabledSpaces

            return { regularSpaces, disabledSpaces, totalSpaces }
        },

        /**
         * Get Total Number Of Spaces Required
         *
         * Calculate the total number of spaces required for the given spaces data (regular and disabled).
         *
         * @param spacesData - the spaces data to calculate the number of spaces required for
         * @returns {number} - the total number of spaces required
         */
        getTotalNumberOfSpacesRequired(spacesData) {
            let regularSpaces = 0
            let disabledSpaces = 0

            // If spacesData has a ReserveList, calculate the number of spaces with a name and email
            if (spacesData[0].parkingSpaceData.parkingSpaceReservedList) {
                const RESERVE_LIST =
                    typeof spacesData[0].parkingSpaceData.parkingSpaceReservedList === 'string'
                        ? JSON.parse(spacesData[0].parkingSpaceData.parkingSpaceReservedList)
                        : spacesData[0].parkingSpaceData.parkingSpaceReservedList
                return RESERVE_LIST.filter((item) => item.reservedName && item.reservedEmail)?.length
            }

            // Otherwise, calculate the number of spaces with a vehicle
            spacesData.forEach((item) => {
                const parkingSpaceData = item.parkingSpaceData
                regularSpaces +=
                    parkingSpaceData.parkingSpaceNumberOfRegularSmallVehiclesRequired +
                    parkingSpaceData.parkingSpaceNumberOfRegularMediumVehiclesRequired +
                    parkingSpaceData.parkingSpaceNumberOfRegularLargeVehiclesRequired
                disabledSpaces +=
                    parkingSpaceData.parkingSpaceNumberOfDisabledSmallVehiclesRequired +
                    parkingSpaceData.parkingSpaceNumberOfDisabledMediumVehiclesRequired +
                    parkingSpaceData.parkingSpaceNumberOfDisabledLargeVehiclesRequired
            })

            return regularSpaces + disabledSpaces
        },

        /**
         * Handle Emitted Menu Selection
         *
         * Take the emitted selection from the menu, and call the relevant function.
         *
         * @param selection a string-based menu option
         */
        handleEmittedMenuSelection(selection) {
            const t = this

            if (selection === 'Event Parking') t.openRightPanel(selection)

            if (selection === 'Export') t.exportData()
        },

        /**
         * Handle Selected Table Rows
         *
         * Format the selected table rows to include the parkingSpaceId and qrColor.
         * This will be used to send the QR code to the selected parking spaces when multiple table rows are selected.
         *
         * @param selectedRows {array} - the selected rows from the table
         */
        handleSelectedTableRows(selectedRows) {
            const t = this

            // Get the EntityId and carParkColour from the selected rows
            t.formattedTableRows = selectedRows.map((row) => {
                return {
                    parkingSpaceId: row.entityId,
                    qrColor: row.parkingData[0].carParkData.carParkColor
                }
            })
        },

        /**
         * Load Car Parks Data
         *
         * Load the required data from the database.
         *
         * @returns {Promise<void>}
         */
        async loadCarParksData() {
            const t = this

            // Fetch the data
            const RESPONSE = await t.MIX_redis_getAll('carPark')

            // Handle any errors
            if (RESPONSE.hasErrors) {
                console.error('Error getting Car Parks: ', RESPONSE.error)
                t.$sharedState.errorMessage = 'There was a problem loading this page, please try again.'
                return
            }

            // Assign the data to the state
            t.carParksData = RESPONSE.data
        },

        /**
         * Load Data
         *
         * Load all the data required for the page.
         *
         * @returns {Promise<void>}
         */
        async loadData() {
            const t = this

            t.isLoading = true

            await Promise.all([
                t.loadCarParksData(),
                t.loadEventsData(),
                t.loadOrganisationsData(),
                t.loadParkingSpacesData(),
                t.loadFullParkingSpacesData()
            ])

            t.isLoading = false
        },

        /**
         * Load Event Data
         *
         * Load the required data from the database.
         *
         * @returns {Promise<void>}
         */
        async loadEventsData() {
            const t = this

            // Fetch the data
            const RESPONSE = await t.MIX_redis_getAll('event')

            // Handle any errors
            if (RESPONSE.hasErrors) {
                console.error('Error getting Events: ', RESPONSE.error)
                t.$sharedState.errorMessage = 'There was a problem loading this page, please try again.'
                return
            }

            // Assign the data to the state
            t.eventsData = RESPONSE.data
        },

        /**
         * Load Full Parking Spaces Data
         *
         * Uses Redis search query for server-side filtering and pagination
         *
         * @returns {Promise<void>}
         */
        async loadFullParkingSpacesData() {
            const t = this

            try {
                // Build Redis search query - start with base filter
                const searchQuery = ['@isDeleted:{false}']

                // Add event name filter
                if (t.searchByEventName) {
                    // Find matching event IDs first
                    const matchingEvents = t.eventsData.filter((event) => event.eventName.toLowerCase().includes(t.searchByEventName.toLowerCase()))

                    if (matchingEvents.length > 0) {
                        // FIXED: Format the OR query properly for Redis Search
                        // The key issue is we need a proper Redis OR syntax
                        if (matchingEvents.length === 1) {
                            // Simple case - just one event
                            searchQuery.push(`@parkingSpaceEventId:{${matchingEvents[0].entityId}}`)
                        } else {
                            // Multiple events - use proper Redis OR syntax without parentheses
                            // Format: @field:({value1}|{value2}|{value3})
                            const eventIds = matchingEvents.map((event) => event.entityId).join('|')
                            searchQuery.push(`@parkingSpaceEventId:{${eventIds}}`)
                        }
                    } else {
                        // No matching events, return empty result early
                        t.fullParkingSpacesData = []
                        return
                    }
                }

                if (t.filterByAttendee) {
                    // Escape any special characters in the search term
                    const escapedAttendee = t.filterByAttendee.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
                    // Redis text search for partial matches - FIXED syntax
                    // only if filterByAttendee is not empty and more than 1 letter
                    if (escapedAttendee.length > 1) {
                        // FIXED: Format the OR query properly for Redis Search
                        // The key issue is we need a proper Redis OR syntax
                        // Format: @field:({value1}|{value2}|{value3})
                        searchQuery.push(`@parkingSpaceIndividualName:${escapedAttendee}*`)
                    }
                }

                // Add car park filter
                if (t.filterByCarParks && t.filterByCarParks.length > 0) {
                    const carParkIds = t.filterByCarParks
                        .map((carParkName) => {
                            const carPark = t.carParksData.find((cp) => cp.carParkName === carParkName)
                            return carPark ? carPark.entityId : null
                        })
                        .filter(Boolean)

                    if (carParkIds.length > 0) {
                        // FIXED: Format the OR query properly for Redis Search
                        if (carParkIds.length === 1) {
                            // Simple case - just one car park
                            searchQuery.push(`@parkingSpaceCarParkId:{${carParkIds[0]}}`)
                        } else {
                            // Multiple car parks - use proper Redis OR syntax
                            const carParkIdsString = carParkIds.join('|')
                            searchQuery.push(`@parkingSpaceCarParkId:{${carParkIdsString}}`)
                        }
                    }
                }

                // Join all parts of the query with spaces (Redis AND operator)
                const queryString = searchQuery.join(' ')

                // Use the correct parameter name 'query' instead of 'redisQuery'
                const RESPONSE = await t.MIX_redis_getFullParkingSpaces({
                    query: queryString,
                    spacesRequired: t.filterBySpacesRequired,
                    page: t.pagination.page,
                    pageSize: t.pagination.limit
                })

                // Handle any errors
                if (RESPONSE.hasErrors) {
                    console.error('Error getting Full Parking Spaces:', RESPONSE.error)
                    t.$sharedState.errorMessage = 'There was a problem loading this page, please try again.'
                    return
                }

                // Assign the data to the state - no client-side filtering needed
                t.fullParkingSpacesData = RESPONSE.data
                t.isLoading = false
            } catch (error) {
                console.error('Error loading parking spaces:', error)
                t.$sharedState.errorMessage = 'There was a problem loading this page, please try again.'
            }
        },

        /**
         * Load Organisations Data
         *
         * Load the required data from the database.
         *
         * @returns {Promise<void>}
         */
        async loadOrganisationsData() {
            const t = this

            // Fetch the data
            const RESPONSE = await t.MIX_redis_getAll('organisation')

            // Handle any errors
            if (RESPONSE.hasErrors) {
                console.error('Error getting Organisations: ', RESPONSE.error)
                t.$sharedState.errorMessage = 'There was a problem loading this page, please try again.'
                return
            }

            // Assign the data to the state
            t.organisationsData = RESPONSE.data
        },

        /**
         * Load Parking Spaces Data
         *
         * Load the required data from the database.
         *
         * @returns {Promise<void>}
         */
        async loadParkingSpacesData() {
            const t = this

            // Fetch the data
            const RESPONSE = await t.MIX_redis_getAll('parkingSpace')

            // Handle any errors
            if (RESPONSE.hasErrors) {
                console.error('Error getting Parking Spaces: ', RESPONSE.error)
                t.$sharedState.errorMessage = 'There was a problem loading this page, please try again.'
                return
            }

            // Assign the data to the state
            t.parkingSpacesData = RESPONSE.data
        },

        /**
         * Open Form
         *
         * Open the form dialog.
         * If no item is passed in, the form will open as new (blank).
         *
         * @param item the selected key
         */
        openRightPanel(item) {
            const t = this

            // If an item is passed in, this is an existing item
            if (item.parkingData?.length) {
                t.isReadOnly = true
                t.selectedItem = { ...item }
                t.tabs = 'current'
                t.shouldFormBeVisible = false
            }

            // Otherwise, create a new item
            else {
                t.isReadOnly = false
                t.selectedItem = {}
                t.tabs = 'form'
                t.selectedParkingItem = {}
                t.shouldFormBeVisible = true
            }

            t.selectedParkingType = 'Event Parking'
            t.isRightPanelVisible = true
        },

        /**
         * Open Send QR Code Dialog
         *
         * Open the send QR code dialog.
         */
        openSendQrCodeDialog() {
            const t = this

            t.isSendQrCodeDialogVisible = true
        },

        /**
         * Handle Send Single Qr Code
         *
         * Send QR codes for each car park in the selected item, using the correct color for each.
         */
        handleSendSingleQrCode() {
            const t = this
            
            // Create an array of individual car park data objects, each with its own id and color
            const carParkQrRequests = t.selectedItem?.parkingData.map(parking => {
                return {
                    parkingSpaceId: parking.parkingSpaceData.entityId,
                    qrColor: parking.carParkData.carParkColor
                }
            }) || []

            // Send QR codes for each car park
            t.sendQrCodes(carParkQrRequests)
        },

        /**
         * Send QR Codes
         *
         * Send the QR codes to the selected parking spaces.
         * Can handle two data formats:
         * 1. From bulk select: formattedTableRows with each entry having parkingSpaceId and qrColor
         * 2. From single item: An array of objects with parkingSpaceId and qrColor properties
         *
         * @param parkingData {array|object} - the parking data to send the QR codes to
         * @returns {Promise<void>}
         */
        async sendQrCodes(parkingData) {
            const t = this

            // Show loading indicator
            if (t.$store && t.$store.commit) {
                t.$store.commit('setLoading', true)
            }

            try {
                // Handle array of parking data (bulk selection or multiple car parks)
                if (Array.isArray(parkingData)) {
                    if (parkingData.length === 0) {
                        t.$sharedState.errorMessage = 'No parking spaces found to send QR codes for.'
                        return
                    }
                    
                    // Create a map to group parking spaces by color
                    const parkingByColor = {}
                    
                    // Group parking spaces by their color
                    parkingData.forEach(item => {
                        const color = item.qrColor || '#666666'
                        if (!parkingByColor[color]) {
                            parkingByColor[color] = []
                        }
                        parkingByColor[color].push(item.parkingSpaceId)
                    })
                    
                    // Track overall success/failure counts
                    let totalSuccessCount = 0
                    let totalFailureCount = 0
                    
                    // Send a separate request for each color group
                    const colorPromises = Object.entries(parkingByColor).map(async ([color, ids]) => {
                        const requestData = {
                            ids: ids,
                            color: color
                        }
                        
                        // Send the properly formatted request for this color group
                        const response = await t.MIX_redis_sendParkingQrCode(requestData)
                        
                        // Handle any errors for this group
                        if (response.hasErrors) {
                            return { 
                                hasErrors: true, 
                                error: response.error,
                                successCount: 0,
                                failureCount: ids.length
                            }
                        }
                        
                        return response
                    })
                    
                    // Wait for all color groups to complete
                    const responses = await Promise.all(colorPromises)
                    
                    // Calculate total success/failure counts
                    responses.forEach(response => {
                        if (response.data && typeof response.data === 'object') {
                            totalSuccessCount += response.data.successCount || 0
                            totalFailureCount += response.data.failureCount || 0
                        }
                    })
                    
                    // Check if any requests had errors
                    const hasErrors = responses.some(response => response.hasErrors)
                    
                    if (hasErrors) {
                        t.$sharedState.warningMessage = 'Some QR codes may not have been sent properly.'
                    }
                    
                    // Show success message with counts
                    t.$sharedState.successMessage = `QR codes sent successfully. ${totalSuccessCount} email(s) sent.`
                    
                    // Show warning if some failed
                    if (totalFailureCount > 0) {
                        t.$sharedState.warningMessage = `${totalFailureCount} email(s) failed to send. Please check logs for details.`
                    }
                }
                // Legacy support for single color request format
                else if (parkingData && parkingData.ids) {
                    const requestData = {
                        ids: parkingData.ids,
                        color: parkingData.color || '#666666'
                    }
                    
                    if (requestData.ids.length === 0) {
                        t.$sharedState.errorMessage = 'No parking spaces found to send QR codes for.'
                        return
                    }
                    
                    // Send the properly formatted request
                    const RESPONSE = await t.MIX_redis_sendParkingQrCode(requestData)
                    
                    // Handle any errors
                    if (RESPONSE.hasErrors) {
                        console.error('Error sending QR code', RESPONSE.error)
                        t.$sharedState.errorMessage = 'There was a problem sending the QR code. Please try again.'
                        return
                    }
                    
                    // Show success message with more details if available
                    if (RESPONSE.data && typeof RESPONSE.data === 'object' && RESPONSE.data.successCount !== undefined) {
                        t.$sharedState.successMessage = `QR code sent successfully. ${RESPONSE.data.successCount} email(s) sent.`
                        
                        // Show warning if some failed
                        if (RESPONSE.data.failureCount > 0) {
                            t.$sharedState.warningMessage = `${RESPONSE.data.failureCount} email(s) failed to send. Please check logs for details.`
                        }
                    } else {
                        t.$sharedState.successMessage = 'QR code sent successfully'
                    }
                }
                else {
                    t.$sharedState.errorMessage = 'Invalid data format for sending QR codes.'
                    return
                }

                t.closeSendQrCodeDialog()
                
                // Clear selected items after successful send
                t.clearAllSelections()
            } catch (error) {
                console.error('Frontend error sending QR code:', error)
                t.$sharedState.errorMessage = 'There was a problem sending the QR code. Please try again.'
            } finally {
                if (t.$store && t.$store.commit) {
                    t.$store.commit('setLoading', false)
                }
            }
        },

        /**
         * Handle Table Selection
         * 
         * Updates the persistentSelectedIds array to maintain selections across pagination
         * 
         * @param {Array} selectedRows - Array of currently selected row objects
         */
        handleTableSelection(selectedRows) {
            const t = this
            
            // Get current IDs on this page
            const currentIds = selectedRows.map(row => row.entityId)
            
            // For newly selected rows - add their IDs to persistentSelectedIds 
            selectedRows.forEach(row => {
                if (!t.persistentSelectedIds.includes(row.entityId)) {
                    t.persistentSelectedIds.push(row.entityId)
                }
            })
            
            // For rows that were deselected on this page - remove from persistentSelectedIds
            const currentTableIds = t.computedTableData.map(row => row.entityId)
            t.persistentSelectedIds = t.persistentSelectedIds.filter(id => {
                // If it's not on the current page OR it's selected, keep it
                return !currentTableIds.includes(id) || currentIds.includes(id)
            })
            
            // Update formattedTableRows for QR code sending based on all IDs
            if (t.persistentSelectedIds.length > 0) {
                t.updateFormattedTableRows()
            } else {
                t.formattedTableRows = []
            }
        },
        
        /**
         * Restore Selections
         * 
         * Restores checkbox selections after pagination
         * by selecting rows whose IDs are in persistentSelectedIds
         */
        restoreSelections() {
            const t = this
            
            // Filter current page data to only include rows with IDs in persistentSelectedIds
            t.selectedTableRows = t.computedTableData.filter(row => 
                t.persistentSelectedIds.includes(row.entityId)
            )
        },
        
        /**
         * Update Formatted Table Rows
         * 
         * Updates formattedTableRows array with data needed for QR codes
         * based on the current persistentSelectedIds
         * For each selected entry, creates a separate object for each car park
         * with its unique id and color.
         */
        updateFormattedTableRows() {
            const t = this
            
            // Start with an empty array to collect all car park QR objects
            let formattedRows = []
            
            // Process each selected ID
            t.persistentSelectedIds.forEach(id => {
                // First check if row is on current page
                const currentPageRow = t.computedTableData.find(row => row.entityId === id)
                
                if (currentPageRow && currentPageRow.parkingData && currentPageRow.parkingData.length > 0) {
                    // For each car park associated with this entity, create a separate QR request
                    currentPageRow.parkingData.forEach(parkingItem => {
                        formattedRows.push({
                            parkingSpaceId: parkingItem.parkingSpaceData.entityId,
                            qrColor: parkingItem.carParkData?.carParkColor || '#666666'
                        })
                    })
                } else {
                    // If not found on current page or data structure is different, we'll need to fetch
                    // the full details for this item from the server
                    
                    // For now, add a placeholder with the entity ID and null color
                    // This will be handled by the backend as a default color
                    formattedRows.push({
                        parkingSpaceId: id,
                        qrColor: null
                    })
                }
            })
            
            t.formattedTableRows = formattedRows
        },
        
        /**
         * Clear All Selections
         * 
         * Clears all selected rows across all pages
         */
        clearAllSelections() {
            const t = this
            
            // Clear selections
            t.selectedTableRows = []
            t.persistentSelectedIds = []
            t.formattedTableRows = []
        },

        /**
         * Toggle Filters Visibility
         *
         * Toggle the visibility of the filters panel.
         */
        toggleFiltersVisibility() {
            const t = this

            t.isFiltersPanelVisible = !t.isFiltersPanelVisible
        },

        /**
         * Get Full Parking Spaces
         *
         * Uses a raw query for advanced searching, just like the users example
         *
         * @param {Object} options - Pagination and query options
         * @param {String} options.query - Raw search query (can be an empty string)
         * @param {Number} options.page - Page number (starts at 1)
         * @param {Number} options.pageSize - Number of items per page
         * @returns {Promise<Object>} - Response with data
         */
        async MIX_redis_getFullParkingSpaces(options = {}) {
            const t = this

            // Set defaults
            const params = {
                query: options.query || '',
                page: options.page || 1,
                pageSize: options.pageSize || 10
            }

            return t.$axios
                .get(`${process.env.VUE_APP_SERVER}/parkingSpace/get/fullParkingSpaces`, {
                    params: params
                })
                .then((response) => {
                    return {
                        data: response.data,
                        hasErrors: false,
                        error: {}
                    }
                })
                .catch((error) => {
                    console.error(`Error getting Full Parking Spaces: `, error)
                    return {
                        data: [],
                        hasErrors: true,
                        error: error
                    }
                })
        }
    },

    watch: {
        /**
         * Selected Table Rows
         *
         * When the selected table rows change, update the selected item.
         * This will not maintain selection across pagination.
         */
        selectedTableRows(selectedRowsData) {
            this.handleSelectedTableRows(selectedRowsData)
            this.handleTableSelection(selectedRowsData)
        },

        /**
         * Search By Event Name
         *
         * When the search term changes, reset to first page and reload data
         */
        searchByEventName() {
            this.pagination.page = 1
            this.dataTableOptions.page = 1
            this.loadFullParkingSpacesData()
        },

        /**
         * Filter by Attendee
         *
         * When the filter changes, reload data
         */
        filterByAttendee() {
            this.pagination.page = 1
            this.dataTableOptions.page = 1
            this.loadFullParkingSpacesData()
        },

        /**
         * Filter by Car Parks
         *
         * When the filter changes, reload data
         */
        filterByCarParks() {
            this.pagination.page = 1
            this.dataTableOptions.page = 1
            this.loadFullParkingSpacesData()
        },

        /**
         * Filter by Spaces Required
         *
         * When the filter changes, reload data
         */
        filterBySpacesRequired() {
            this.pagination.page = 1
            this.dataTableOptions.page = 1
            this.loadFullParkingSpacesData()
        }
    },

    async created() {
        const t = this

        // Initialize dataTableOptions from pagination for consistency
        t.dataTableOptions.page = t.pagination.page
        t.dataTableOptions.itemsPerPage = t.pagination.limit

        await t.loadData()
    }
}
</script>

<style scoped></style>
